import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Download, AlertTriangle, X } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PLAN_LABEL = { pro_plus: "Pro Plus", pro: "Pro", free: "Free" };
const FLAG_LABEL = {
  missing_ein: "Missing EIN",
  incomplete_business_address: "Incomplete address",
  unfunded: "No funding source",
  payouts_disabled: "Payouts disabled",
  no_employees: "No employees",
  no_authorized_rail: "No authorized rail",
  unauthorized_rails: "Unauthorized rails",
  never_ran_payroll: "Never ran payroll",
};

const COLUMNS = [
  { key: "name", label: "Company" },
  { key: "plan", label: "Plan" },
  { key: "mrr_usd", label: "MRR", num: true },
  { key: "employees", label: "Employees", num: true },
  { key: "payroll_runs", label: "Runs", num: true },
  { key: "gross_paid", label: "Gross paid", num: true },
  { key: "net_paid", label: "Net paid", num: true },
  { key: "last_pay_date", label: "Last payroll" },
  { key: "funding", label: "Funding" },
  { key: "risk_flags", label: "Risk" },
];

function Stat({ label, value, testid }) {
  return (
    <div className="border-2 border-ink p-4 bg-white" data-testid={testid}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-heading font-black text-xl mt-1 break-words">{value}</div>
    </div>
  );
}

export default function EstateView() {
  const [state, setState] = useState({ loading: true, allowed: false });
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "mrr_usd", dir: "desc" });
  const [drill, setDrill] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/estate/overview");
      setData(data);
      setState({ loading: false, allowed: true });
    } catch {
      setState({ loading: false, allowed: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const list = [...(data?.companies || [])];
    const { key, dir } = sort;
    list.sort((a, b) => {
      const av = key === "risk_flags" ? (a.risk_flags || []).length : a[key];
      const bv = key === "risk_flags" ? (b.risk_flags || []).length : b[key];
      if (typeof av === "number" && typeof bv === "number") return dir === "asc" ? av - bv : bv - av;
      const as = String(av ?? ""), bs = String(bv ?? "");
      return dir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return list;
  }, [data, sort]);

  const openDrill = async (id) => {
    setDrill({ loading: true, id });
    try {
      const { data } = await api.get(`/estate/company/${id}`);
      setDrill((cur) => (cur && cur.id === id ? { ...data, id } : cur));
    } catch (e) {
      setDrill((cur) => (cur && cur.id === id ? null : cur));
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = () => {
    const head = ["Company", "State", "Plan", "MRR", "Employees", "Admins", "Runs", "Gross paid", "Net paid", "Taxes withheld", "Last payroll", "Funding", "Risk flags"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      `"${(r.name || "").replace(/"/g, '""')}"`, r.state, r.plan, r.mrr_usd, r.employees, r.admins,
      r.payroll_runs, r.gross_paid, r.net_paid, r.taxes_withheld, r.last_pay_date || "",
      r.funding, `"${(r.risk_flags || []).join(" | ")}"`,
    ].join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `checkmate-estate-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (state.loading) return <div className="font-mono text-sm p-6">LOADING…</div>;
  if (!state.allowed) {
    return (
      <div className="font-mono p-6" data-testid="estate-not-found">
        <h1 className="text-4xl font-black">404</h1>
        <p className="text-sm mt-2">Not found.</p>
      </div>
    );
  }

  const t = data.totals;
  return (
    <div className="p-6 space-y-8" data-testid="estate-view">
      <div className="border-2 border-ink p-6 bg-secondary/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black flex items-center gap-3">
              <Crown className="w-7 h-7" style={{ color: "#B08D3F" }} /> ESTATE VIEW
            </h1>
            <p className="text-sm mt-2 max-w-2xl">
              Every company on Checkmate, across tenants. Visible only to the Root Admin —
              reached from the Founder's Mark.
            </p>
            <p className="font-mono text-xs mt-2">ROOT ADMIN · {data.root_admin}</p>
          </div>
          <button
            onClick={exportCsv}
            data-testid="estate-export-csv"
            className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase bg-white hover:bg-ink hover:text-white transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="estate-totals">
        <Stat label="Companies" value={t.companies} testid="estate-total-companies" />
        <Stat label="Employees" value={t.employees} testid="estate-total-employees" />
        <Stat label="MRR" value={money(t.mrr_usd)} testid="estate-total-mrr" />
        <Stat label="ARR" value={money(t.arr_usd)} testid="estate-total-arr" />
        <Stat label="Payroll runs" value={t.payroll_runs} testid="estate-total-runs" />
        <Stat label="Gross paid" value={money(t.gross_paid)} testid="estate-total-gross" />
        <Stat label="Taxes withheld" value={money(t.taxes_withheld)} testid="estate-total-taxes" />
        <Stat label="Companies at risk" value={t.at_risk} testid="estate-total-at-risk" />
      </div>

      <div className="border-2 border-ink bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="estate-table">
          <thead>
            <tr className="border-b-2 border-ink">
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === "desc" ? "asc" : "desc" }))}
                  data-testid={`estate-sort-${c.key}`}
                  className={`px-3 py-3 font-mono text-[10px] uppercase tracking-widest cursor-pointer select-none whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}
                >
                  {c.label}{sort.key === c.key ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => openDrill(r.id)}
                data-testid={`estate-row-${r.id}`}
                className="border-b border-ink/20 hover:bg-secondary/60 cursor-pointer"
              >
                <td className="px-3 py-3 font-heading font-black">
                  {r.name}
                  <span className="block font-mono font-normal text-[10px] opacity-60">{r.state}</span>
                </td>
                <td className="px-3 py-3 font-mono text-xs">
                  {PLAN_LABEL[r.plan]}{r.loyalty ? " · loyalty" : ""}
                </td>
                <td className="px-3 py-3 font-mono text-xs text-right">{money(r.mrr_usd)}</td>
                <td className="px-3 py-3 font-mono text-xs text-right">{r.employees}</td>
                <td className="px-3 py-3 font-mono text-xs text-right">{r.payroll_runs}</td>
                <td className="px-3 py-3 font-mono text-xs text-right">{money(r.gross_paid)}</td>
                <td className="px-3 py-3 font-mono text-xs text-right">{money(r.net_paid)}</td>
                <td className="px-3 py-3 font-mono text-xs">{r.last_pay_date || "—"}</td>
                <td className="px-3 py-3 font-mono text-xs">{r.funding.replace(/_/g, " ")}</td>
                <td className="px-3 py-3">
                  {(r.risk_flags || []).length === 0 ? (
                    <span className="font-mono text-xs opacity-50">clear</span>
                  ) : (
                    <span className="flex flex-wrap gap-1" data-testid={`estate-risk-${r.id}`}>
                      {r.risk_flags.map((f) => (
                        <span key={f} className="inline-flex items-center gap-1 border border-ink px-1.5 py-0.5 font-mono text-[9px] uppercase" style={{ background: "#FDE9C8" }}>
                          <AlertTriangle className="w-2.5 h-2.5" />{FLAG_LABEL[f] || f}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drill && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="estate-drill">
          <div className="bg-white border-2 border-ink max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between border-b-2 border-ink p-4">
              <h3 className="font-black text-base md:text-lg">
                {drill.loading ? "LOADING…" : `${drill.company.name} · read-only`}
              </h3>
              <button onClick={() => setDrill(null)} data-testid="estate-drill-close"
                className="border-2 border-ink px-3 py-1 font-mono text-xs uppercase flex items-center gap-1">
                <X className="w-3 h-3" /> Close
              </button>
            </div>
            <div className="p-4 overflow-auto space-y-6">
              {drill.loading ? (
                <p className="font-mono text-xs">FETCHING TENANT…</p>
              ) : (
                <>
                  <div className="grid gap-3 grid-cols-2 md:grid-cols-4 font-mono text-xs">
                    <div>PLAN · {PLAN_LABEL[drill.company.plan]}</div>
                    <div>MRR · {money(drill.company.mrr_usd)}</div>
                    <div>EMPLOYEES · {drill.company.employees}</div>
                    <div>RUNS · {drill.company.payroll_runs}</div>
                    <div>GROSS · {money(drill.company.gross_paid)}</div>
                    <div>NET · {money(drill.company.net_paid)}</div>
                    <div>TAXES · {money(drill.company.taxes_withheld)}</div>
                    <div>FUNDING · {drill.company.funding.replace(/_/g, " ")}</div>
                  </div>
                  <div>
                    <h4 className="overline mb-2">Roster ({drill.roster.length})</h4>
                    <div className="border-2 border-ink" data-testid="estate-drill-roster">
                      {drill.roster.length === 0 && <p className="p-3 font-mono text-xs">No employees.</p>}
                      {drill.roster.map((e) => (
                        <div key={e.id} className="flex items-center justify-between gap-3 border-b border-ink/20 px-3 py-2 font-mono text-xs">
                          <span className="font-heading font-black">{e.name}</span>
                          <span className="opacity-70">{e.status} · {e.employment_type} · {e.state}</span>
                          <span>{e.rail_authorized ? "rail authorized" : "rail dormant"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="overline mb-2">Recent payroll ({drill.recent_runs.length})</h4>
                    <div className="border-2 border-ink" data-testid="estate-drill-runs">
                      {drill.recent_runs.length === 0 && <p className="p-3 font-mono text-xs">No payroll runs.</p>}
                      {drill.recent_runs.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-3 border-b border-ink/20 px-3 py-2 font-mono text-xs">
                          <span>{r.pay_date}</span>
                          <span className="opacity-70">{r.period_start} → {r.period_end}</span>
                          <span>{money(r.gross)} gross · {money(r.net_pay)} net</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
